import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE, NativeDateAdapter } from '@angular/material/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  differenceInCalendarDays,
  endOfDay,
  endOfMonth,
  format,
  isSameDay,
  isValid,
  isWithinInterval,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';

import {
  CanvasElementPropertiesWithData,
  DEFAULT_ELEMENT_PADDING,
  getBackground,
  getBorderColor,
  makeBorderStyles,
  makeContainerVisualEffectStyles,
  makeFontStyleStyles,
  makeLabelHorizontalAlignStyles,
} from '@blaze-shared/elements-domain';
import {
  BlazeSchema,
  convertBlazeSchemaToElementSchema,
  DatasetRow,
} from '@blaze-shared/schema-domain';
import { resizeRxUtils, trackByIndex, useEmitTracker } from '@blaze-shared/shared-util';
import { FALLBACK_THEME } from '@blaze-shared/theming-domain';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { CalendarDatesViewComponent } from '../../components/calendar-dates-view';
import { ContainerActionsService } from '../../services/container-actions.service';
import { CanvasElementBaseComponent } from '../__base/base.component';

import { findFirstAvailableDay, pageRangeContainingDay } from './scheduler-default-day.utils';

interface ScheduleItem {
  day: Date;
  timeSlots: TimeSlot[];
  isToday: boolean;
}

interface TimeSlot {
  time: Date;
  dataRow: any;
}

interface DateRange {
  from: Date;
  to: Date;
}

interface SerializedDateRange {
  from: string;
  to: string;
}

export enum DaySelectorMode {
  DEFAULT = 'DEFAULT',
  CALENDAR = 'CALENDAR',
}

export enum DefaultDayMode {
  TODAY = 'TODAY',
  FIRST_AVAILABLE = 'FIRST_AVAILABLE',
}

const DAY_MINIMUM_WIDTH_PX = 64;

@UntilDestroy()
@Component({
  selector: 'be-scheduler',
  templateUrl: './scheduler.component.html',
  styleUrls: ['./scheduler.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: DateAdapter,
      useClass: NativeDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
  ],
  standalone: false,
})
export class SchedulerComponent
  extends CanvasElementBaseComponent<'scheduler'>
  implements CanvasElementComponent<'scheduler'>, AfterViewInit, OnChanges, OnInit
{
  private dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
  private containerActionsService = inject(ContainerActionsService);
  private document = inject<Document>(DOCUMENT);

  @Input() value: DatasetRow[] | undefined;

  @ViewChild('daysList') daysListRef?: ElementRef<HTMLUListElement>;
  @ViewChild(CalendarDatesViewComponent) calendar!: CalendarDatesViewComponent;

  @HostBinding('style.--b-sch-font-size') fontSize = FALLBACK_THEME.value.fontSize;
  @HostBinding('style.--b-sch-font-family') fontFamily = FALLBACK_THEME.value.fontFamily;
  @HostBinding('style.--b-sch-line-height') lineHeight = FALLBACK_THEME.value.lineHeight;
  @HostBinding('style.--b-sch-title-text-color') titleTextColor = FALLBACK_THEME.value.title;
  @HostBinding('style.--b-sch-accent-color') accentColor = FALLBACK_THEME.value.accent;
  @HostBinding('style.--b-sch-background') hostBackground = 'initial';
  @HostBinding('style.--b-sch-border-radius') hostBorderRadius = 'initial';
  @HostBinding('style.--b-sch-internal-padding') internalPadding? = DEFAULT_ELEMENT_PADDING;

  // Days
  @HostBinding('style.--b-sch-d-text-color') dTextColor = FALLBACK_THEME.value.title;
  @HostBinding('style.--b-sch-d-fill-color') dFillColor = FALLBACK_THEME.value.fill;
  @HostBinding('style.--b-sch-d-outline-color') dOutlineColor = 'transparent';
  @HostBinding('style.--b-sch-d-outline-width') dOutlineWidth = '1px';
  @HostBinding('style.--b-sch-d-radius') dRadius = '24px';

  // Hours
  @HostBinding('style.--b-sch-h-text-color') hTextColor = FALLBACK_THEME.value.title;
  @HostBinding('style.--b-sch-h-fill-color') hFillColor = 'none';
  @HostBinding('style.--b-sch-h-outline-color') hOutlineColor = FALLBACK_THEME.value.outline;
  @HostBinding('style.--b-sch-h-outline-width') hOutlineWidth = '1px';
  @HostBinding('style.--b-sch-h-radius') hRadius = FALLBACK_THEME.value.radius;

  // Date Selector - default
  @HostBinding('style.--b-cr-date-selector-default-text') dateSelectorDefaultText = '#000';
  @HostBinding('style.--b-cr-date-selector-default-background') dateSelectorDefaultBackground =
    '#000';
  @HostBinding('style.--b-cr-date-selector-default-border-color') dateSelectorDefaultBorderColor =
    'transparent';
  @HostBinding('style.--b-cr-date-selector-default-border-width') dateSelectorDefaultBorderWidth =
    '1px';
  @HostBinding('style.--b-cr-date-selector-default-border-radius') dateSelectorDefaultBorderRadius =
    '16px';

  // Date Selector - hover
  @HostBinding('style.--b-cr-date-selector-hover-text') dateSelectorHoverText = '#000';
  @HostBinding('style.--b-cr-date-selector-hover-background') dateSelectorHoverBackground = '#000';
  @HostBinding('style.--b-cr-date-selector-hover-border-color') dateSelectorHoverBorderColor =
    '#00f';
  @HostBinding('style.--b-cr-date-selector-hover-border-width') dateSelectorHoverBorderWidth =
    '1px';
  @HostBinding('style.--b-cr-date-selector-hover-border-radius') dateSelectorHoverBorderRadius =
    '16px';

  // Date Selector - hover
  @HostBinding('style.--b-cr-date-selector-selected-text') dateSelectorSelectedText = '#fff';
  @HostBinding('style.--b-cr-date-selector-selected-background') dateSelectorSelectedBackground =
    '#00f';
  @HostBinding('style.--b-cr-date-selector-selected-border-color') dateSelectorSelectedBorderColor =
    'transparent';
  @HostBinding('style.--b-cr-date-selector-selected-border-width') dateSelectorSelectedBorderWidth =
    '1px';
  @HostBinding('style.--b-cr-date-selector-selected-border-radius')
  dateSelectorSelectedBorderRadius = '16px';

  @HostBinding('style.--b-cr-date-selector-day-with-events') dateSelectorDayWithEvents = '#333';

  private readonly dataValue = this.getDataInput('value');
  private readonly MOBILE_THRESHOLD_PX = 550;
  private readonly MOBILE_MAX_DAYS_SHOWN = 3;

  private computedValueCache$: Observable<Record<string, TimeSlot[]>>;
  private daysListElement$ = new BehaviorSubject<HTMLUListElement | undefined>(undefined);
  private startColumnName$ = new BehaviorSubject<string | undefined>(undefined);
  private maxForwardDays$ = new BehaviorSubject<number>(0);
  private reset$ = new Subject<void>();
  private wasEmitted = false;
  private preventDefaultDayJump = false;
  private defaultDayMode: DefaultDayMode = DefaultDayMode.TODAY;
  private value$: Observable<any> = this.dataValue.data$.pipe(distinctUntilChanged());
  private outputEmitTracker = useEmitTracker();

  readonly DaySelectorMode = DaySelectorMode;
  readonly elementKeyRoot = 'b-sch';

  elementKey!: string;

  labelEnabled!: boolean;
  labelText!: string;
  isReadonly!: boolean;
  emptyListLabel!: string;
  daySelectorMode: DaySelectorMode = DaySelectorMode.DEFAULT;
  wrapperStyles: Record<string, string> | null = null;
  inputStyles!: Record<string, string>;
  slotsMultipleListStyles$: Observable<Record<string, string>>;

  trackByIndex = trackByIndex;

  dayOfWeekNames: string[] = [];
  monthNames: string[] = [];

  advanceRange$ = new Subject<number>();
  selectedDateRange$ = new BehaviorSubject<DateRange | undefined>(undefined);
  selectedDay$ = new BehaviorSubject<Date | undefined>(undefined);
  selectedScheduleDay$: Observable<ScheduleItem | undefined>;
  selectedMonthLabel$: Observable<string>;

  currentPage$: Observable<ScheduleItem[]>;
  daysShown$ = new BehaviorSubject<number>(1);
  pageSize$: Observable<number>;
  canGoToNextPage$: Observable<boolean>;
  canGoToPrevPage$: Observable<boolean>;
  isOnePageLayout$: Observable<boolean>;
  noSlotsInSelectedRange$: Observable<boolean>;
  datesWithEvents$: Observable<Date[]>;
  isCalendarVisible = true;

  get selectedDate(): Date {
    return this.selectedDay$.getValue() ?? new Date();
  }

  get isCalendarMode(): boolean {
    return this.daySelectorMode === DaySelectorMode.CALENDAR;
  }

  constructor() {
    super();
    const today = this.dateAdapter.today();

    this.dayOfWeekNames = this.dateAdapter.getDayOfWeekNames('short');
    this.monthNames = this.dateAdapter.getMonthNames('long');

    /**
     * Calculate page size.
     * If days shown option is set to 1 then display list of days based
     * on container width with no more than 7 days.
     */
    this.pageSize$ = this.daysShown$.pipe(
      switchMap((days) => {
        if (days === 1) {
          return this.daysListElement$.pipe(
            filter((el): el is HTMLUListElement => !!el),
            switchMap((el) =>
              resizeRxUtils.fromResizeObserver(el).pipe(
                debounceTime(100),
                map((entries) => {
                  const entry = entries[0];
                  // Minimum size of single column
                  return Math.min(
                    Math.max(Math.floor(entry.contentRect.width / DAY_MINIMUM_WIDTH_PX), 1),
                    7,
                  );
                }),
              ),
            ),
          );
        }
        return of(days);
      }),
      distinctUntilChanged(),
    );

    /**
     * When page size changes reset date range.
     */
    this.pageSize$
      .pipe(
        map((pageSize) => ({
          from: today,
          to: this.dateAdapter.addCalendarDays(today, pageSize - 1),
        })),
        untilDestroyed(this),
      )
      .subscribe((range) => {
        this.selectedDateRange$.next(range);
      });

    /**
     * Determine layout type.
     * If days shown equals 1 then day can be selected
     * and available slots are displayed only for that day.
     * If days show are higher than 1 then slots are displayed on every day and day cannot be selected.
     */
    this.isOnePageLayout$ = this.daysShown$.pipe(map((days) => days === 1));

    /**
     * Group time slots by day and sort them from earliest to latest.
     */
    this.computedValueCache$ = combineLatest({
      startColumnName: this.startColumnName$.pipe(distinctUntilChanged()),
      value: this.value$.pipe(distinctUntilChanged()),
    }).pipe(map(({ startColumnName, value }) => this.createTimeSlots(value, startColumnName)));

    this.datesWithEvents$ = combineLatest({
      startColumnName: this.startColumnName$.pipe(filter(Boolean), distinctUntilChanged()),
      value: this.value$.pipe(filter(Boolean), distinctUntilChanged()),
    }).pipe(
      map(({ startColumnName, value }) =>
        value
          .map((v: DatasetRow) => this.dateAdapter.deserialize(v[startColumnName]))
          .filter(Boolean),
      ),
    );

    this.currentPage$ = combineLatest({
      range: this.selectedDateRange$,
      value: this.computedValueCache$.pipe(startWith({} as Record<string, TimeSlot[]>)),
      maxForwardDays: this.maxForwardDays$,
    }).pipe(
      filter(({ range }) => !!range),
      map(({ range, value, maxForwardDays }) => {
        if (this.daySelectorMode === DaySelectorMode.CALENDAR && !this.isCalendarVisible) {
          // return all appointments for the calendar view
          return Object.keys(value).map((dateString) => ({
            day: new Date(dateString),
            timeSlots: value[dateString],
            isToday: this.isSameDay(new Date(dateString), today),
          }));
        }

        if (!range) {
          return [];
        }
        if (this.isCalendarVisible) {
          range = {
            from: startOfMonth(range.from),
            to: endOfMonth(range.to),
          };
        }

        const days: Date[] = [range.from];
        if (!this.isSameDay(range.from, range.to)) {
          let generatedDay = range.from;
          do {
            generatedDay = this.dateAdapter.addCalendarDays(generatedDay, 1);
            days.push(generatedDay);
          } while (!this.isSameDay(generatedDay, range.to));
        }

        return days.map((day) => {
          const isDayWithinMaxForward = differenceInCalendarDays(day, today) < maxForwardDays;
          return {
            day,
            timeSlots:
              this.isCalendarVisible || isDayWithinMaxForward
                ? (value[this.getDayLabel(day)] ?? [])
                : [],
            isToday: this.isSameDay(day, today),
          };
        });
      }),
    );

    this.advanceRange$
      .pipe(
        withLatestFrom(this.selectedDateRange$, this.pageSize$),
        map(([delta, selectedDateRange, pageSize]): DateRange | undefined => {
          if (!selectedDateRange) {
            return;
          }
          if (delta === 0) {
            return selectedDateRange;
          }
          return this.moveDateRange(selectedDateRange, pageSize, delta);
        }),
        untilDestroyed(this),
      )
      .subscribe((range) => {
        this.selectedDateRange$.next(range);
      });

    this.canGoToNextPage$ = combineLatest({
      selectedDateRage: this.selectedDateRange$,
      maxForwardDays: this.maxForwardDays$,
    }).pipe(
      map(({ selectedDateRage, maxForwardDays }) => {
        if (maxForwardDays === 0 || !selectedDateRage) {
          return false;
        }
        const diff = differenceInCalendarDays(selectedDateRage.to, today);
        return diff < maxForwardDays;
      }),
    );

    this.canGoToPrevPage$ = combineLatest({
      selectedDateRage: this.selectedDateRange$,
      maxForwardDays: this.maxForwardDays$,
    }).pipe(
      map(({ selectedDateRage }) => {
        if (!selectedDateRage) {
          return false;
        }
        return (
          selectedDateRage.from.getTime() > today.getTime() &&
          selectedDateRage.from.getDate() !== today.getDate()
        );
      }),
    );

    this.selectedMonthLabel$ = this.selectedDateRange$.pipe(
      filter(Boolean),
      map((range) => {
        const monthFrom = this.dateAdapter.getMonth(range.from);
        const yearFrom = this.dateAdapter.getYear(range.from);
        const monthTo = this.dateAdapter.getMonth(range.to);
        const yearTo = this.dateAdapter.getYear(range.to);
        const monthFromName = this.monthNames[monthFrom];
        const monthToName = this.monthNames[monthTo];

        if (yearFrom !== yearTo) {
          return `${monthFromName} ${yearFrom} / ${monthToName} ${yearTo}`;
        }
        if (monthFrom !== monthTo) {
          return `${monthFromName} / ${monthToName} ${yearFrom}`;
        }
        return `${monthFromName} ${yearFrom}`;
      }),
    );

    this.selectedScheduleDay$ = combineLatest({
      day: this.selectedDay$,
      page: this.currentPage$,
    }).pipe(map(({ day, page }) => page.find((item) => this.isSameDay(item.day, day))));

    this.selectedDateRange$
      .pipe(
        withLatestFrom(this.selectedDay$, this.isOnePageLayout$),
        map(([range, selected, isOnePageLayout]) => {
          if (!isOnePageLayout || !range) {
            return;
          }

          const toSelect = selected || today;

          if (
            isWithinInterval(toSelect, { start: startOfDay(range.from), end: endOfDay(range.to) })
          ) {
            return toSelect;
          }

          return range.from;
        }),
        untilDestroyed(this),
      )
      .subscribe((dayToSelect) => {
        this.selectedDay$.next(dayToSelect);
      });

    combineLatest({
      slotsByDay: this.computedValueCache$,
      range: this.selectedDateRange$.pipe(filter(Boolean)),
    })
      .pipe(
        filter(({ slotsByDay }) => Object.keys(slotsByDay).length > 0),
        take(1),
        untilDestroyed(this),
      )
      .subscribe(({ slotsByDay, range }) => this.jumpToFirstAvailableDay(slotsByDay, range));

    this.noSlotsInSelectedRange$ = combineLatest({
      page: this.currentPage$,
      selectedScheduleDay: this.selectedScheduleDay$,
      isOnePageLayout: this.isOnePageLayout$,
    }).pipe(
      map(({ page, selectedScheduleDay, isOnePageLayout }) => {
        if (isOnePageLayout) {
          return !selectedScheduleDay?.timeSlots.length;
        }
        return !page.some((p) => p.timeSlots.length);
      }),
    );

    this.slotsMultipleListStyles$ = this.currentPage$.pipe(
      map((page) => ({
        'grid-template-columns': `repeat(${page.length}, 1fr)`,
      })),
    );

    /**
     * Handle output.
     */
    merge(this.currentPage$, this.reset$.pipe(tap(() => this.outputEmitTracker.reset())))
      .pipe(
        withLatestFrom(this.currentPage$),
        filter(([_, page]) => page?.length > 0),
        map(([_, page]) => {
          const firstDay = page[0].day;
          const lastDay = page[page.length - 1].day;
          return this.serializeOutputDateRange({
            from: firstDay,
            to: lastDay,
          });
        }),
        untilDestroyed(this),
      )
      .subscribe((serialized) => {
        this.updateOutput(serialized);
      });

    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['props']) {
      if (!this.elementKey) {
        this.elementKey = `${this.elementKeyRoot}-${this.elementId}`;
      }
      const { title, input } = this.props?.content || {};

      this.labelEnabled = title?.enabled ?? true;
      this.labelText = title?.label || '';
      this.isReadonly = input?.readonly || false;
      this.emptyListLabel = input?.emptyListLabel ?? 'No appointments available';
      if (input?.daySelector) {
        this.daySelectorMode = input.daySelector;
      }
      this.defaultDayMode = input?.defaultDay ?? DefaultDayMode.TODAY;
      this.isCalendarVisible = this.daySelectorMode === DaySelectorMode.CALENDAR;

      this.startColumnName$.next(this.getColumnName(input?.startTimeColumn));
      this.maxForwardDays$.next(parseInt(input?.maxForwardDays || '1', 10));

      this.updateDaysShown();
      this.updateStyles();
    }
  }

  ngOnInit(): void {
    this.setDefaultEventSchema('change');

    if (!this.bindingResults?.['value']) {
      this.setDefaultEventSchema('action');
    }

    this.dataValue.schema$
      .pipe(untilDestroyed(this), filter(Boolean))
      .subscribe((schema: BlazeSchema) => {
        this.setDefaultEventSchema(
          'action',
          convertBlazeSchemaToElementSchema(schema, this.elementId, 'action'),
        );
      });
  }

  ngAfterViewInit(): void {
    this.daysListElement$.next(this.daysListRef?.nativeElement);

    fromEvent(window, 'resize')
      .pipe(
        map(() => this.showMobileLayout()),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.updateDaysShown();
      });
  }

  goToNextPage(): void {
    this.preventDefaultDayJump = true;
    this.advanceRange$.next(1);
  }

  goToPrevPage(): void {
    this.preventDefaultDayJump = true;
    this.advanceRange$.next(-1);
  }

  getDayName(date: Date): string {
    return this.dayOfWeekNames[this.dateAdapter.getDayOfWeek(date)];
  }

  getDayNumber(date: Date): number {
    return this.dateAdapter.getDate(date);
  }

  getDayLabel(date: Date): string {
    return this.dateAdapter.format(date, 'yyyy-MM-dd');
  }

  getHourLabel(date: Date): string {
    return format(date, 'hh:mma').toLocaleLowerCase();
  }

  onAction(event: Event, slot: TimeSlot): void {
    event?.stopPropagation();

    if (slot.dataRow === null || !this.props?.content.action) {
      return;
    }

    const dataSource = (this.props as CanvasElementPropertiesWithData)?.data?.find(
      (data) => data?.key === 'value',
    )?.dataSource;
    if (!dataSource) {
      return console.error(`Action failed. Data source not configured.`);
    }

    const dataRowSchema = this.getEventSchema('action');
    this.containerActionsService.handleSchedulerAction(
      slot.dataRow,
      dataRowSchema,
      this.props?.content.action,
      this.output,
    );
  }

  isDaySelected(date: Date): boolean {
    const day = this.selectedDay$.value;
    if (!day) {
      return false;
    }
    return this.isSameDay(date, day);
  }

  selectDay(date: Date | undefined): void {
    this.preventDefaultDayJump = true;
    this.selectedDay$.next(date);
  }

  selectCalendarDay(date: Date | undefined): void {
    this.preventDefaultDayJump = true;
    this.selectedDay$.next(date);
    const range = date ? { from: date, to: date } : undefined;
    this.selectedDateRange$.next(range);
    if (date) {
      this.calendar.focusCell(date);
    }
  }

  selectPreviousDate(): void {
    const previous = new Date(this.selectedDate);
    previous.setDate(previous.getDate() - 1);
    this.selectCalendarDay(previous);
  }

  selectNextDate(): void {
    const next = new Date(this.selectedDate);
    next.setDate(next.getDate() + 1);
    this.selectCalendarDay(next);
  }

  selectPreviousMonth(): void {
    const previous = new Date(this.selectedDate);
    previous.setMonth(previous.getMonth() - 1);
    previous.setDate(1);

    this.selectCalendarDay(previous);
  }

  selectNextMonth(): void {
    const next = new Date(this.selectedDate);
    next.setMonth(next.getMonth() + 1);
    next.setDate(1);

    this.selectCalendarDay(next);
  }

  reset(): void {
    this.reset$.next();
  }

  private createTimeSlots(
    value: DatasetRow[] | undefined,
    columnName: string | undefined,
  ): Record<string, TimeSlot[]> {
    const cache: Record<string, TimeSlot[]> = {};

    if (!Array.isArray(value) || !columnName) {
      return cache;
    }

    value.forEach((v) => {
      const date = this.dateAdapter.deserialize(v[columnName]);

      if (!date || !isValid(date)) {
        return;
      }

      const dayString = this.getDayLabel(date);

      if (!cache[dayString]) {
        cache[dayString] = [];
      }

      cache[dayString].push({ time: date, dataRow: v });
    });

    Object.keys(cache).forEach((key) => {
      cache[key] = cache[key].sort((a, b) => a.time.getTime() - b.time.getTime());
    });

    return cache;
  }

  private isSameDay(dateA?: Date, dateB?: Date): boolean {
    if (!dateA && !dateB) {
      return true;
    }
    if (!dateA || !dateB) {
      return false;
    }

    return isSameDay(dateA, dateB);
  }

  private jumpToFirstAvailableDay(slotsByDay: Record<string, TimeSlot[]>, range: DateRange): void {
    if (
      this.defaultDayMode !== DefaultDayMode.FIRST_AVAILABLE ||
      this.daySelectorMode !== DaySelectorMode.DEFAULT ||
      this.preventDefaultDayJump
    ) {
      return;
    }

    const firstAvailableDay = findFirstAvailableDay({
      slotsByDay,
      today: this.dateAdapter.today(),
      maxForwardDays: this.maxForwardDays$.value,
      getDayKey: (date) => this.getDayLabel(date),
    });
    if (!firstAvailableDay) {
      return;
    }

    const targetRange = pageRangeContainingDay(range, firstAvailableDay);
    if (targetRange !== range) {
      // Re-emits `change`, so a loop-back workflow reloads slots for the new page.
      this.selectedDateRange$.next(targetRange);
    }

    const isOnePageLayout = this.daysShown$.value === 1;
    if (isOnePageLayout) {
      this.selectedDay$.next(firstAvailableDay);
    }
  }

  private moveDateRange(range: DateRange, pageSize: number, delta: number): DateRange {
    return {
      from: this.dateAdapter.addCalendarDays(range.from, pageSize * delta),
      to: this.dateAdapter.addCalendarDays(range.to, pageSize * delta),
    };
  }

  private serializeOutputDateRange(range: DateRange): SerializedDateRange {
    return {
      from: format(range.from, 'yyyy-MM-dd'),
      to: format(range.to, 'yyyy-MM-dd'),
    };
  }

  private updateOutput(data: SerializedDateRange): void {
    if (this.outputEmitTracker.shouldEmit(data)) {
      this.output.emit({
        eventType: 'change',
        data,
        schema: this.getEventSchema('change'),
        isInitialData: !this.wasEmitted,
      });
      this.wasEmitted = true;
    }
  }

  private updateStyles(): void {
    const { style, content } = this.props || {};
    const { title } = content || {};
    const {
      background,
      dateSelector,
      days,
      fontFamily,
      fontSize,
      general,
      hours,
      lineHeight,
      titleBlock,
    } = style || {};
    if (
      !background ||
      !dateSelector ||
      !days ||
      !general ||
      !hours ||
      !style ||
      !title ||
      !titleBlock
    ) {
      return;
    }

    const labelStyles: Record<string, string> = {
      color: general.title.color1,
      ...makeLabelHorizontalAlignStyles(title.align),
      ...makeFontStyleStyles(titleBlock.fontStyle),
      fontFamily: titleBlock.fontFamily ?? 'var(--bat-headerFontFamily)',
      fontSize: titleBlock.fontSize ?? 'var(--bat-headerFontSize)',
      fontWeight: titleBlock.fontWeight ?? 'var(--bat-headerFontWeight)',
    };
    const inputStyles: Record<string, string> = {};

    if (title.iconPosition === 'right') {
      labelStyles['flex-direction'] = `row-reverse`;
    }

    this.internalPadding = general.offset.padding;

    this.fontSize = fontSize as string;
    this.fontFamily = fontFamily as string;
    this.lineHeight = lineHeight as string;

    this.titleTextColor = general.title.color1;
    this.accentColor = general.accent.color1;

    this.dTextColor = days.text.color1;
    this.dFillColor = getBackground(days.fill);
    this.dOutlineColor = getBorderColor(days.outline);
    this.dOutlineWidth = days.outline.selectorType === 'none' ? '0px' : days.outlineWidth;
    this.dRadius = days.radius;

    this.hTextColor = hours.text.color1;
    this.hFillColor = getBackground(hours.fill);
    this.hOutlineColor = getBorderColor(hours.outline);
    this.hOutlineWidth = hours.outline.selectorType === 'none' ? '0px' : hours.outlineWidth;
    this.hRadius = hours.radius;

    this.hostBackground = getBackground(background.fill);
    this.hostBorderRadius = background.radius;

    const { default: def, hover, selected } = dateSelector;
    this.dateSelectorDefaultText = def.day.color1;
    this.dateSelectorDefaultBackground = def.fill.color1;
    this.dateSelectorDefaultBorderColor =
      def.outline.selectorType === 'none' ? 'transparent' : def.outline.color1;
    this.dateSelectorDefaultBorderWidth = def.outlineWidth;
    this.dateSelectorDefaultBorderRadius = def.radius;

    this.dateSelectorHoverText = hover.day.color1;
    this.dateSelectorHoverBackground = hover.fill.color1;
    this.dateSelectorHoverBorderColor =
      hover.outline.selectorType === 'none' ? 'transparent' : hover.outline.color1;
    this.dateSelectorHoverBorderWidth = hover.outlineWidth;
    this.dateSelectorHoverBorderRadius = hover.radius;

    this.dateSelectorSelectedText = selected.day.color1;
    this.dateSelectorSelectedBackground = selected.fill.color1;
    this.dateSelectorSelectedBorderColor =
      selected.outline.selectorType === 'none' ? 'transparent' : selected.outline.color1;
    this.dateSelectorSelectedBorderWidth = selected.outlineWidth;
    this.dateSelectorSelectedBorderRadius = selected.radius;
    this.dateSelectorDayWithEvents = dateSelector.dayWithEvents.color1;

    this.wrapperStyles = {
      ...makeBorderStyles(background.outline, background.outlineWidth),
      ...makeContainerVisualEffectStyles(background.visualEffect),
    };
    this.inputStyles = inputStyles;
  }

  private showMobileLayout(): boolean {
    return this.document.body.clientWidth <= this.MOBILE_THRESHOLD_PX;
  }

  private updateDaysShown(): void {
    const daysShown = this.getDaysShownValue();
    if (this.daysShown$.value !== daysShown) {
      this.daysShown$.next(daysShown);
    }
  }

  private getDaysShownValue(): number {
    if (this.daySelectorMode === DaySelectorMode.CALENDAR) {
      return 1;
    }

    let daysShown = this.props?.content.input?.daysShown || 1;

    if (this.showMobileLayout() && daysShown > this.MOBILE_MAX_DAYS_SHOWN) {
      daysShown = this.MOBILE_MAX_DAYS_SHOWN;
    }

    return daysShown;
  }
}
